/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Offcanvas } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import warehouseService from 'service/warehouse';
import Button from 'components/base/Button';
import legalEntitySettingsService from 'service/legalEntitySettings';
import ndsService from 'service/nds';
import { showAlert } from 'store/alert/alert.thunk';
import productSearchService from 'service/productSearch';
import ShowSerialNumber from 'pages/reports/purchase/ShowSerialNumber';
import './styles.scss';

export type AcceptanceType = {
  product_name: string;
  barcode: string;
  currency: string;
  units: string;
  status: string;
  create_date: string;
  guid: string;
  quantity_fact: any;
  quantity: any;
  cost: string;
  discrepancies_accept: any;
  discrepancies: any;
  serial_number: any;
  price_wholesale: any;
  price_wholesale_org: any;
  price: any;
  price_in: any;
  measure_unit_pid: any;
  package_quantity: any;
  measure_unit_kind_pid: any;
  proved_quantity_by_deliver: any;
  total_price: any;
  is_remove_table_selected: boolean;
};

const SwitchControl = ({
  field,
  label,
  disabled
}: {
  field: any;
  label: any;
  disabled: any;
}) => {
  return (
    <Form.Check
      disabled={disabled}
      type="switch"
      label={label}
      className="cursor-pointer"
      checked={field.value}
      onChange={e => field.onChange(e.target.checked)}
    />
  );
};

interface AcceptanceOfApplication {
  changedOrderItems?: any;
  setChangedOrderItems?: any;
  setOpenModal: (show: boolean) => void;
  refetch?: any;
  refetchOut?: any;
  isControl?: boolean;
}

const AcceptanceOfApplication = ({
  changedOrderItems,
  setChangedOrderItems,
  setOpenModal,
  refetch,
  refetchOut,
  isControl
}: AcceptanceOfApplication) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const orderPid = Number(pathname.split('/').pop());
  const { control, register, getValues, setValue, watch } = useForm({
    defaultValues: {
      update_sell: false,
      is_vat: false,
      markup: '',
      markup_wholesale: '',
      warehouse: ''
    }
  });
  const cashboxId = useSelector((state: any) => state?.shiftId?.shiftId);
  const cashboxPid = useSelector((state: any) => state?.shiftId?.shiftPid);
  const dispatch: Dispatch<any> = useDispatch();

  const [warehouseOption, setWarehouseOption] = useState<any>([]);
  const [ndsCost, setNdsCost] = useState('');
  const [ndsWholsale, setNdsWholsale] = useState('');
  const [nds, setNds] = useState('');
  const [showSerialNumber, setShowSerialNumber] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const updateSell = watch('update_sell');

  //GET Warehouse Entity to options
  useQuery(['GET_WAREHOUSE'], async () => {
    await warehouseService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => {
        const options = res?.ware_houses?.map((option: any) => ({
          value: option.id,
          pid: option?.pid || 0,
          label: option.name
        }));
        setWarehouseOption(options);
      });
  });

  //GET Warehouse Entity to options
  useQuery(['GET_SETTING'], async () => {
    await legalEntitySettingsService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => {
        setNdsCost(res?.legal_entity_settings[0].extra_charge);
        setNdsWholsale(res?.legal_entity_settings[0].extra_charge_wholesale);
        setValue('markup', res?.legal_entity_settings[0].extra_charge);
        setValue(
          'markup_wholesale',
          res?.legal_entity_settings[0].extra_charge_wholesale
        );
      });
  });

  //GET Warehouse Entity to options
  useQuery(['GET_NDS'], async () => {
    await ndsService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        setNds(res?.vats[0].value);
      });
  });

  useEffect(() => {
    if (!changedOrderItems?.length || isControl) return;

    const updatedItems = changedOrderItems.map((item: any) => {
      const purchasePrice = isControl === false ? +item.cost : +item.price_in;

      const basePriceWholesale = purchasePrice || 0;
      const costMultiplier = +ndsCost || 0; // Assume ndsCost is a percentage (e.g., 20 for 20%)
      const wholesaleMultiplier = +ndsWholsale || 0; // Assume ndsWholesale is a percentage

      return {
        ...item,
        price_wholesale_org: updateSell
          ? 0
          : basePriceWholesale +
            (basePriceWholesale * wholesaleMultiplier) / 100,
        price: updateSell
          ? 0
          : basePriceWholesale + (basePriceWholesale * costMultiplier) / 100
      };
    });
    setChangedOrderItems(updatedItems);
  }, [updateSell, ndsCost, ndsWholsale, isControl]);

  const acceptanceTableColumns: ColumnDef<AcceptanceType>[] = [
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'units',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'price_in',
      header: t('purchase_price'),
      cell: rowData => {
        const { price_in, cost } = rowData.row.original;
        const purchasePrice = isControl === false ? cost : price_in;
        return (
          <div>
            {purchasePrice
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(purchasePrice)
                  .replace(/,/g, ' ')
              : 0}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },

    {
      accessorKey: 'price_wholesale_org',
      header: t('wholesale_price'),
      cell: rowData => {
        const {
          price_wholesale,
          price_wholesale_org,
          is_remove_table_selected
        } = rowData.row.original;
        const initialValue =
          price_wholesale_org ||
          (updateSell
            ? 0
            : +price_wholesale + (+price_wholesale * +ndsWholsale) / 100);

        const formatWithSpaces = (val: any) => {
          const cleanValue = String(val || '').replace(/[^0-9.]/g, ''); // Remove non-numeric chars except .
          if (!cleanValue) return '';
          const [integerPart, decimalPart] = cleanValue.includes('.')
            ? cleanValue.split('.')
            : [cleanValue, ''];
          const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ' '
          );
          return decimalPart
            ? `${formattedInteger}.${decimalPart}`
            : formattedInteger;
        };

        // Function to remove spaces for state storage
        const removeSpaces = (val: string) => {
          return val.replace(/\s/g, '');
        };

        const [value, setValue] = useState(
          formatWithSpaces(String(initialValue))
        );

        const updateProductInvoiceData = (
          name: string,
          value: string,
          index: any
        ) => {
          const cleanValue = removeSpaces(value);
          const updatedItems = changedOrderItems?.map((item: any, i: any) => {
            if (i === +index) {
              return {
                ...item,
                [name]: +cleanValue,
                ['amount_discrepancies']: +item?.quantity * +cleanValue
              };
            }
            return item;
          });
          setChangedOrderItems(updatedItems);
        };

        const onBlur = (e: React.ChangeEvent<HTMLInputElement>, index: any) => {
          const { name, value } = e.target;
          const cleanValue = removeSpaces(value);
          const formattedValue = cleanValue.replace(/[^0-9.]/g, '');
          updateProductInvoiceData(name, formattedValue, index);
          setValue(formatWithSpaces(formattedValue));
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          const cleanValue = removeSpaces(newValue);

          const decimalPattern = /^-?\d*[.]?\d*$/;

          if (decimalPattern.test(cleanValue)) {
            setValue(formatWithSpaces(cleanValue));
          }
        };

        return (
          <div>
            <Form.Group>
              <Form.Control
                disabled={isControl || is_remove_table_selected}
                type="text"
                name="price_wholesale_org"
                value={value}
                onChange={handleChange}
                onBlur={(e: any) => onBlur(e, rowData.row?.id)}
                onFocus={e => e.target.select()}
                style={{ textAlign: 'right' }}
              />
            </Form.Group>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'price',
      header: t('price_difference'),
      cell: rowData => {
        const { price_wholesale, price, is_remove_table_selected } =
          rowData.row.original;
        const initialValue =
          price ||
          (updateSell
            ? 0
            : +price_wholesale + (+price_wholesale * +ndsCost) / 100);

        const formatWithSpaces = (val: any) => {
          const cleanValue = String(val || '').replace(/[^0-9.]/g, ''); // Remove non-numeric chars except .
          if (!cleanValue) return '';
          const [integerPart, decimalPart] = cleanValue.includes('.')
            ? cleanValue.split('.')
            : [cleanValue, ''];
          const formattedInteger = integerPart.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ' '
          );
          return decimalPart
            ? `${formattedInteger}.${decimalPart}`
            : formattedInteger;
        };

        // Function to remove spaces for state storage
        const removeSpaces = (val: string) => {
          return val.replace(/\s/g, '');
        };

        const [value, setValue] = useState(
          formatWithSpaces(String(initialValue))
        );

        const updateProductInvoiceData = (
          name: string,
          value: string,
          index: any
        ) => {
          const cleanValue = removeSpaces(value);
          const updatedItems = changedOrderItems?.map((item: any, i: any) => {
            if (i === +index) {
              return { ...item, [name]: +cleanValue };
            }
            return item;
          });
          setChangedOrderItems(updatedItems);
        };

        const onBlur = (e: React.ChangeEvent<HTMLInputElement>, index: any) => {
          const { name, value } = e.target;
          const cleanValue = removeSpaces(value);
          const formattedValue = cleanValue.replace(/[^0-9.]/g, '');
          updateProductInvoiceData(name, formattedValue, index);
          setValue(formatWithSpaces(formattedValue));
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;
          const cleanValue = removeSpaces(newValue);

          const decimalPattern = /^-?\d*[.]?\d*$/;

          if (decimalPattern.test(cleanValue)) {
            setValue(formatWithSpaces(cleanValue));
          }
        };

        return (
          <div>
            <Form.Group>
              <Form.Control
                disabled={isControl || is_remove_table_selected}
                type="text"
                name="price"
                value={value}
                onChange={handleChange}
                onBlur={(e: any) => onBlur(e, rowData.row?.id)}
                onFocus={e => e.target.select()}
                style={{ textAlign: 'right' }}
              />
            </Form.Group>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'amount',
      header: t('application_quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity_fact',
      header: t('short_quantity_shipment'),
      cell: rowData => {
        const { package_quantity, measure_unit_kind_pid, quantity_fact } =
          rowData.row.original;

        const packageQuantity = package_quantity || 1;

        const quantity = quantity_fact ? Number(quantity_fact.toFixed(3)) : 0;

        const fullPackages = Math.floor(quantity / packageQuantity);
        const remainingQuantity = quantity % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        return (
          <div style={{ fontWeight: 'bold', color: 'red' }}>
            {measure_unit_kind_pid === 1 ? quantityDisplay : quantity_fact}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'quantity',
      header: t('short_quantity_fact'),
      cell: rowData => {
        const {
          package_quantity,
          measure_unit_kind_pid,
          quantity,
          proved_quantity_by_deliver,
          is_remove_table_selected
        } = rowData.row.original;

        const packageQuantity = package_quantity || 1;

        const quantityShort = quantity ? Number(quantity.toFixed(3)) : 0;

        const fullPackages = Math.floor(quantityShort / packageQuantity);
        const remainingQuantity = quantityShort % packageQuantity;
        const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

        const [value, setValue] = useState(
          measure_unit_kind_pid === 1 ? quantityDisplay : quantityShort
        );

        const updateProductInvoiceData = (
          name: string,
          value: string,
          index: any
        ) => {
          // Validate input based on measure_unit_pid
          let numValue: number;
          if (measure_unit_kind_pid === 2) {
            if (!/^-?\d*$/.test(value)) {
              dispatch(
                showAlert({
                  title: t('invalid_quantity_integer'),
                  type: 'error'
                })
              );
              return;
            }
            numValue = parseInt(value, 10);
          } else if (measure_unit_kind_pid === 1) {
            if (value.includes('/')) {
              if (!/^-?\d*\/\d*$/.test(value)) {
                dispatch(
                  showAlert({
                    title: t('invalid_quantity_fraction'),
                    type: 'error'
                  })
                );
                return;
              }

              const quantityNew = value || '0';

              let integerPartNew = 0;
              let numeratorNew = 0;
              let denominatorNew = 0; // Default denominator to 1 if no fraction

              const quantityStrNew = String(quantityNew);
              // Check if quantity contains '/'
              if (quantityStrNew?.includes('/')) {
                [numeratorNew, denominatorNew] = quantityStrNew
                  .split('/')
                  .map(Number);

                integerPartNew = numeratorNew; // Whole number part
                // eslint-disable-next-line
                denominatorNew = denominatorNew; // Remainder of the division
              } else {
                integerPartNew = +quantityStrNew;
              }

              // Calculate adjusted quantity
              const adjustedQuantity =
                measure_unit_kind_pid === 1
                  ? integerPartNew * packageQuantity + denominatorNew
                  : integerPartNew;

              numValue = +adjustedQuantity;
            } else {
              numValue = parseInt(value, 10);
            }
          } else {
            if (!/^-?\d*[.,]?\d*$/.test(value)) {
              dispatch(
                showAlert({
                  title: t('invalid_quantity_decimal'),
                  type: 'error'
                })
              );
              return;
            }
            numValue = parseFloat(value.replace(',', '.'));
          }

          // Ensure numValue is valid
          if (isNaN(numValue)) {
            dispatch(
              showAlert({
                title: t('select_product_add_quantity'),
                type: 'error'
              })
            );
            return;
          }

          if (numValue > proved_quantity_by_deliver) {
            dispatch(
              showAlert({
                title: `${t('quantity_error')}: ${proved_quantity_by_deliver}`
              })
            );
            setValue(
              measure_unit_kind_pid === 1
                ? quantityDisplay
                : quantityShort.toString()
            );
            return;
          }

          const updatedItems = changedOrderItems?.map((item: any, i: any) => {
            if (i === +index) {
              const newQuantity = +item?.quantity_fact - numValue;

              const fullPackages = Math.floor(newQuantity / packageQuantity);
              const remainingQuantity = newQuantity % packageQuantity;
              const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

              return {
                ...item,
                [name]: numValue,
                ['discrepancies_accept']:
                  measure_unit_kind_pid === 1 ? quantityDisplay : newQuantity
              };
            }
            return item;
          });
          setChangedOrderItems(updatedItems);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;

          // Pattern for integers (e.g., "5", "-5")
          const integerPattern = /^-?\d*$/;
          // Pattern for fractions (e.g., "5/2", "-5/2", allows single slash)
          const fractionPattern = /^-?\d*\/?\d*$/;
          // Pattern for decimal numbers (e.g., "123.45", "-123.45", allows . or ,)
          const decimalPattern = /^-?\d*[.,]?\d*$/;

          // Allow empty string to clear input
          if (!newValue) {
            setValue('');
            return;
          }

          let isPatternValid = false;

          if (measure_unit_kind_pid === 2) {
            // Only allow integers, no /, ., or ,
            if (
              integerPattern.test(newValue) &&
              !newValue.includes('.') &&
              !newValue.includes(',') &&
              !newValue.includes('/')
            ) {
              isPatternValid = true;
            }
          } else if (measure_unit_kind_pid === 1) {
            // Only allow fraction pattern (with /), no . or ,
            if (
              fractionPattern.test(newValue) &&
              !newValue.includes('.') &&
              !newValue.includes(',')
            ) {
              isPatternValid = true;
            }
          } else {
            // Allow decimal numbers (with . or ,)
            if (decimalPattern.test(newValue)) {
              isPatternValid = true;
            }
          }

          if (isPatternValid) {
            // Preliminary validation against proved_quantity_by_deliver
            let tempNumValue: number;
            if (measure_unit_kind_pid === 2) {
              tempNumValue = parseInt(newValue, 10);
            } else if (measure_unit_kind_pid === 1) {
              if (newValue.includes('/')) {
                const [numerator, denominator] = newValue
                  .split('/')
                  .map(Number);
                tempNumValue = numerator * packageQuantity + (denominator || 0);
              } else {
                tempNumValue = parseInt(newValue, 10);
              }
            } else {
              tempNumValue = parseFloat(newValue.replace(',', '.'));
            }

            if (
              !isNaN(tempNumValue) &&
              tempNumValue > proved_quantity_by_deliver
            ) {
              dispatch(
                showAlert({
                  title: `${t('quantity_error')}: ${proved_quantity_by_deliver}`
                })
              );
              return;
            }

            setValue(newValue);
          } else {
            dispatch(
              showAlert({
                title: t('invalid_quantity_format'),
                type: 'error'
              })
            );
          }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          // Allow specific keys based on measure_unit_pid
          if (measure_unit_kind_pid === 2) {
            // Integers: allow numbers, backspace, delete, arrows, minus
            if (
              !/[0-9]/.test(e.key) &&
              e.key !== 'Backspace' &&
              e.key !== 'Delete' &&
              e.key !== 'ArrowLeft' &&
              e.key !== 'ArrowRight' &&
              e.key !== '-'
            ) {
              e.preventDefault();
            }
          } else if (measure_unit_kind_pid === 1) {
            // Fractions: allow numbers, /, backspace, delete, arrows, minus
            if (
              !/[0-9/]/.test(e.key) &&
              e.key !== 'Backspace' &&
              e.key !== 'Delete' &&
              e.key !== 'ArrowLeft' &&
              e.key !== 'ArrowRight' &&
              e.key !== '-'
            ) {
              e.preventDefault();
            }
          } else {
            // Decimals: allow numbers, ., ,, backspace, delete, arrows, minus
            if (
              !/[0-9.,]/.test(e.key) &&
              e.key !== 'Backspace' &&
              e.key !== 'Delete' &&
              e.key !== 'ArrowLeft' &&
              e.key !== 'ArrowRight' &&
              e.key !== '-'
            ) {
              e.preventDefault();
            }
          }
        };

        const onBlur = (e: React.ChangeEvent<HTMLInputElement>, index: any) => {
          const { name, value } = e.target;
          console.log('name', name);
          console.log('value', value);
          updateProductInvoiceData(name, value, index);
        };

        return (
          <div>
            <Form.Group>
              <Form.Control
                disabled={is_remove_table_selected}
                type="text"
                name="quantity"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={(e: any) => onBlur(e, rowData.row?.id)}
                onFocus={e => e.target.select()}
              />
            </Form.Group>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'discrepancies_accept',
      header: t('short_no_expoenses'),
      // cell: rowData => {
      //   const quantityFact = +rowData?.row?.original?.quantity_fact;
      //   const quantity = +rowData?.row?.original?.quantity;

      //   return <div>{quantityFact - quantity}</div>;
      // },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'amount_discrepancies',
      header: t('asmount_discrepancy'),
      cell: rowData => {
        const {
          cost,
          price_in,
          discrepancies_accept,
          measure_unit_kind_pid,
          package_quantity
        } = rowData.row.original;

        const quantity = discrepancies_accept || '0';

        let integerPart = 0;
        let numerator = 0;
        let denominator = 0; // D

        const quantityStr = String(quantity);
        // Check if quantity contains '/'
        if (quantityStr?.includes('/')) {
          [numerator, denominator] = quantityStr.split('/').map(Number);

          integerPart = numerator; // Whole number part
          // eslint-disable-next-line
          denominator = denominator; // Remainder of the division
        } else {
          integerPart = +quantityStr;
        }

        const packageQuantity = +package_quantity || 1;

        const adjustedQuantity =
          measure_unit_kind_pid === 1
            ? integerPart * packageQuantity + denominator
            : integerPart;

        const purchasePrice = isControl === false ? +cost : +price_in;

        const total_price =
          measure_unit_kind_pid === 1
            ? (purchasePrice / package_quantity) * +adjustedQuantity
            : purchasePrice * +adjustedQuantity;

        return (
          <div>
            {total_price
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(total_price)
                  .replace(/,/g, ' ')
              : 0}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },

    {
      accessorKey: 'total_price',
      header: t('final_price'),
      cell: rowData => {
        const {
          cost,
          price_in,
          quantity,
          measure_unit_kind_pid,
          package_quantity
        } = rowData.row.original;

        const purchasePrice = isControl === false ? +cost : +price_in;

        const total_price =
          measure_unit_kind_pid === 1
            ? (purchasePrice / package_quantity) * +quantity
            : purchasePrice * +quantity;

        return (
          <>
            {total_price
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(total_price)
                  .replace(/,/g, ' ')
              : 0}
          </>
        );
      },
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'serial_number',
      header: t('serial_number'),
      cell: ({ row: { original } }) => {
        return (
          <div>
            {original.serial_number?.length > 0 && (
              <Button
                variant="soft-primary"
                onClick={() => {
                  setShowSerialNumber(true);
                  setSelectedRow(original);
                }}
              >
                {t('show')}
              </Button>
            )}
          </div>
        );
      },
      meta: {
        cellProps: {
          className: 'text-900',
          style: { whiteSpace: 'nowrap' }
        }
      }
    }
  ];

  const table = useAdvanceTable({
    data: changedOrderItems,
    columns: acceptanceTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true,
    tableOptions: {
      getRowCanSelect: (row: any) => {
        const quantity = Number(row.original.quantity || 0);
        console.log('quantity', quantity);
        return quantity !== 0;
      }
    }
  });

  const handleAccept = () => {
    const { warehouse } = getValues();
    if (!warehouse || warehouse === undefined) {
      dispatch(showAlert({ title: t('warehouse_must_selected') }));
      return;
    }
    const selectedDates = table
      .getSelectedRowModel()
      .rows?.map(row => row.original);

    const createdData = selectedDates
      ?.map((items: any) => {
        const quantity = +items?.quantity || 0;
        if (quantity === 0) {
          return null;
        }

        return {
          name: items?.product_name,
          barcode: items?.barcode,
          quantity_fact: items?.quantity_fact,
          quantity: +items?.quantity || 0,
          price_in: isControl === false ? +items?.cost : +items?.price_in,
          price_whosale: items?.price_wholesale_org ?? 0,
          price: +items?.price || 0,
          nomenclature_id: items?.nomenclature_id,
          nomenclature_pid: items?.nomenclature_pid || 0,
          measure_unit_kind_pid: items?.measure_unit_kind_pid,
          package_quantity: items?.package_quantity || 1,
          parent_product_id: items?.product_id,
          parent_product_pid: items?.product_pid || 0,
          product_id: items?.product_id,
          product_pid: items?.product_pid || 0,
          order_item_id: items?.guid,
          order_item_pid: items?.gupid || 0,
          vat: +nds || 0,
          partion_codes: items?.serial_number || []

          //   package_quantity: +item?.amount ?? 0,
          //  is_vat: isVatPayerValue,
        };
      })
      .filter(item => item !== null);

    const selectedWarehouse = warehouseOption?.find(
      (item: any) => item?.value === warehouse
    );

    const newSavedData = {
      invoice: {
        cashbox_id: cashboxId,
        cashbox_pid: cashboxPid || 0,
        warehouse_id: warehouse,
        warehouse_pid: selectedWarehouse?.pid || 0
      },
      order_pid: orderPid || 0,
      product: createdData
    };

    if (
      cashboxId !== '' &&
      createdData?.length > 0 &&
      warehouse !== undefined
    ) {
      setLoading(true);
      productSearchService.createProductOrder(newSavedData).then((res: any) => {
        if (res) {
          setLoading(false);
          setOpenModal(false);
          setChangedOrderItems([]);
          refetch();
          refetchOut();
          dispatch(
            showAlert({
              title: t('application_successfully_accepted'),
              type: 'success'
            })
          );
        }
      });
    } else {
      if (cashboxId === '') {
        dispatch(showAlert({ title: t('shift_not_open') }));
      }
      if (warehouse === undefined) {
        dispatch(showAlert({ title: t('warehouse_must_selected') }));
      }

      if (createdData?.length === 0) {
        dispatch(showAlert({ title: t('no_item_selected') }));
      }
    }
  };

  console.log('changedOrderItems', changedOrderItems);
  const sum = changedOrderItems.reduce((acc: any, item: any) => {
    const purchasePrice = isControl === false ? +item?.cost : +item?.price_in;

    const total_price =
      item?.measure_unit_kind_pid === 1
        ? (purchasePrice / item?.package_quantity) * +item?.quantity
        : purchasePrice * +item?.quantity;

    const allCost = total_price === undefined ? 0 : total_price;

    return acc + allCost;
  }, 0);

  useEffect(() => {
    if (warehouseOption?.length === 1) {
      setValue('warehouse', warehouseOption[0].value);
    }
  }, [warehouseOption, setValue]);

  return (
    <div>
      <Form noValidate className="app-rows mb-4">
        <Form.Floating>
          <Form.Control
            {...register('markup')}
            disabled={isControl}
            type="number"
            placeholder={t('markup_in')}
            value={ndsCost}
            onChange={e => setNdsCost(e?.target?.value)}
            onFocus={e => e.target.select()}
          />
          <label htmlFor="floatingInputCustom">{t('markup_in')}</label>
        </Form.Floating>

        <Form.Floating>
          <Form.Control
            {...register('markup_wholesale')}
            disabled={isControl}
            type="number"
            placeholder={t('markup_wholesale')}
            value={ndsWholsale}
            onChange={e => setNdsWholsale(e?.target?.value)}
            onFocus={e => e.target.select()}
          />
          <label htmlFor="floatingInputCustom">{t('markup_wholesale')}</label>
        </Form.Floating>

        <Form.Group>
          <div className="react-select-container">
            <Controller
              name="warehouse"
              control={control}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {warehouseOption?.map((option: any) => (
                      <option
                        key={option.value}
                        value={option.value}
                        className="option"
                      >
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('warehouse')}</label>
                </Form.Floating>
              )}
            />
          </div>
        </Form.Group>

        <Form.Group className="d-flex align-items-center my-auto">
          <Controller
            name="update_sell"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <SwitchControl
                field={field}
                label={t('update_sale_price')}
                disabled={isControl}
              />
            )}
          />
        </Form.Group>

        <Form.Group className="d-flex align-items-center my-auto">
          <Controller
            name="is_vat"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <SwitchControl
                field={field}
                label={t('variable_product')}
                disabled={isControl}
              />
            )}
          />
        </Form.Group>
      </Form>

      <AdvanceTableProvider {...table}>
        <div>
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <div className="d-flex justify-content-end align-items-center gap-3 mt-3">
        <div
          style={{
            border: '1px solid #8200bf',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '20px',
            alignItems: 'center',
            padding: '8px'
          }}
        >
          <p className="mb-0 text-bold">{t('total_amount')}:</p>
          <span>
            {sum
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(sum)
                  .replace(/,/g, ' ')
              : 0}
          </span>
        </div>

        <Button
          loading={loading}
          loadingPosition="start"
          variant="primary"
          onClick={() => handleAccept()}
        >
          {t('accept')}
        </Button>
      </div>

      <Offcanvas
        show={showSerialNumber}
        onHide={() => {
          setShowSerialNumber(false);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
        style={{ zIndex: 1060 }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('serial_number')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <ShowSerialNumber selectedRow={selectedRow} />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default AcceptanceOfApplication;
